/**
 * partnerSchedule.ts — the one figure a Vehicle Schedule cannot be generated without [D-B48, D-B59].
 *
 * THE FIGURE IS THE CONTRACT'S CASH PRICE, READ AND NEVER TYPED. `agreement_create` copies
 * `contracts.cash_price`, rounded to the pesewa, onto the referent at generation (the column is
 * `contract_partner_agreements.all_in_cost`, a name kept for history only) and the page prints it as
 * the Cash price. Every derived figure on the page divides by it: the net return is the expected
 * total less it, the annualised return is that net over it, and the capital-recovery period is it
 * over the partner's weekly share. So a contract that states none cannot raise a schedule.
 *
 * THE CORRECTION IS MADE ON FINANCIALS, NOT HERE. On a DRAFT contract a changed cash price
 * withdraws the schedule (the money watcher), and the next generation copies the corrected figure.
 *
 * PURE, AND REACT-FREE, for the reason `deposits/depositAgreement.ts` is: `vitest.config.ts` runs
 * `src/**\/*.test.ts` under node, so a rule that lives in a `.tsx` component is a rule no test can
 * reach. What is here is exactly the refusal; the dialog holds the rest of the copy.
 */

/**
 * The one sentence the board and the generate dialog both say where a schedule cannot be raised.
 * The server's own refusal (`AGREEMENT_CASH_PRICE_REQUIRED`) keeps its mapped sentence in
 * `lib/agreements/constants.ts`.
 */
const NO_CASH_PRICE =
    'This contract states no cash price. Enter it on Financials, then generate the schedule.';

/**
 * Why the Vehicle Schedule cannot be generated from this cash price, or null.
 *
 * THE BOUNDARY IS THE FIGURE THE SERVER WOULD STORE, NOT THE ONE ON THE CONTRACT. `agreement_create`
 * refuses a NULL or non-positive `contracts.cash_price` with `AGREEMENT_CASH_PRICE_REQUIRED`, then
 * copies `round(cash_price, 2)` into a column carrying `CHECK (all_in_cost IS NULL OR
 * all_in_cost > 0)`. A cash price that is above zero but rounds to 0.00 (0.004) passes the first
 * test and fails the CHECK, so the pesewa rounding is what is tested here. Zero is not a cheaper
 * vehicle; it is the absence of an answer, and every figure the schedule derives would divide by
 * it. A non-finite number is the same absence arriving by another road.
 *
 * THE ONE RULE, READ BY BOTH SURFACES: `boardSlots` blocks the row on it and
 * `GeneratePartnerScheduleDialog` holds Generate shut on it, so the two cannot disagree.
 */
export function partnerScheduleCashPriceRefusal(
    cashPrice: number | null | undefined,
): string | null {
    if (cashPrice === null || cashPrice === undefined || !Number.isFinite(cashPrice)) {
        return NO_CASH_PRICE;
    }
    return Math.round(cashPrice * 100) / 100 <= 0 ? NO_CASH_PRICE : null;
}
